import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Download, ExternalLink, Loader2, Paperclip, Pencil, Trash2, X } from 'lucide-react'
import {
  fetchLegalAct, fetchLegalActArticles, fetchLegalActVersions, updateLegalArticle, deleteLegalArticle, deleteLegalAct,
  downloadLegalActFile, LegalAct, LegalArticle, LegalArticleUpdate,
} from '../../lib/api/api'
import { useAuth } from '../../contexts/AuthContext'
import { useAttachments } from '../../contexts/AttachmentContext'
import LegalStatusBadge from '../upload/components/LegalStatusBadge'
import OfficialSourceNotice, { isOfficialSource } from '../../shared/components/OfficialSourceNotice'
import VirtualizedArticleList from '../../shared/components/VirtualizedArticleList'

type Props = {
  actId: number
  onClose?: () => void
  /** Called after the act itself is deleted (super admin only). */
  onDeleted?: () => void
}

type ArticleForm = {
  label: string
  rubric: string
  section_title: string
  content: string
}

const toForm = (article: LegalArticle): ArticleForm => ({
  label: article.label || '',
  rubric: article.rubric || '',
  section_title: article.section_title || '',
  content: article.content || '',
})

export default function LegalActDetail({ actId, onClose, onDeleted }: Props) {
  const { t } = useTranslation()
  const { canManageCorpus } = useAuth()
  const { attachDocument, detachDocument, isDocumentAttached } = useAttachments()

  const [currentId, setCurrentId] = useState(actId)
  const [act, setAct] = useState<LegalAct | null>(null)
  const [articles, setArticles] = useState<LegalArticle[]>([])
  const [versions, setVersions] = useState<Array<{ id: number; language: string }>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDeletingAct, setIsDeletingAct] = useState(false)

  const [editingArticle, setEditingArticle] = useState<LegalArticle | null>(null)
  const [editForm, setEditForm] = useState<ArticleForm>({ label: '', rubric: '', section_title: '', content: '' })
  const [isSavingArticle, setIsSavingArticle] = useState(false)

  useEffect(() => {
    setCurrentId(actId)
  }, [actId])

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError(null)
    Promise.all([fetchLegalAct(currentId), fetchLegalActArticles(currentId)])
      .then(([actRes, articlesRes]) => {
        if (!active) return
        setAct(actRes.data)
        setArticles(articlesRes.data)
      })
      .catch(() => {
        if (active) setError(t('legalActs.errors.loadFailed'))
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => { active = false }
  }, [currentId, t])

  useEffect(() => {
    let active = true
    fetchLegalActVersions(currentId)
      .then((res) => {
        if (active) setVersions(res.data)
      })
      .catch(() => {
        if (active) setVersions([])
      })
    return () => { active = false }
  }, [currentId])

  if (isLoading || !act) {
    return (
      <div className="flex items-center justify-center p-12">
        {isLoading ? <Loader2 className="animate-spin text-gray-400" /> : (error && <p className="text-red-600">{error}</p>)}
      </div>
    )
  }

  const attachId = String(act.id)
  const isAttached = isDocumentAttached(attachId, 'legal_act')

  const handleAttachToggle = () => {
    if (isAttached) {
      detachDocument(attachId, 'legal_act')
    } else {
      attachDocument({ id: attachId, title: act.title, type: 'legal_act', doc_type: 'legal_act', file_name: act.title })
    }
  }

  const handleDeleteAct = async () => {
    if (!window.confirm(t('legalActs.detail.deleteActConfirm', { title: act.title }))) return
    setIsDeletingAct(true)
    try {
      await deleteLegalAct(act.id)
      onDeleted?.()
    } catch {
      setError(t('legalActs.errors.deleteFailed'))
    } finally {
      setIsDeletingAct(false)
    }
  }

  const handleDeleteArticle = async (article: LegalArticle) => {
    if (!window.confirm(t('legalActs.detail.deleteArticleConfirm'))) return
    try {
      await deleteLegalArticle(act.id, article.id)
      setArticles((prev) => prev.filter((a) => a.id !== article.id))
    } catch {
      setError(t('legalActs.errors.deleteArticleFailed'))
    }
  }

  const openEditArticle = (article: LegalArticle) => {
    setEditingArticle(article)
    setEditForm(toForm(article))
  }

  const handleSaveArticle = async () => {
    if (!editingArticle) return
    setIsSavingArticle(true)
    try {
      const body: LegalArticleUpdate = {
        label: editForm.label,
        rubric: editForm.rubric,
        section_title: editForm.section_title,
        content: editForm.content,
      }
      const response = await updateLegalArticle(act.id, editingArticle.id, body)
      setArticles((prev) => prev.map((a) => (a.id === editingArticle.id ? response.data : a)))
      setEditingArticle(null)
    } catch {
      setError(t('legalActs.errors.saveArticleFailed'))
    } finally {
      setIsSavingArticle(false)
    }
  }

  const officialSource = isOfficialSource(act.official_source) ? act.official_source : undefined

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
            >
              <ArrowLeft size={16} />
              {t('legalActs.detail.back')}
            </button>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{act.title}</h2>
            <LegalStatusBadge
              legal_status={act.legal_status}
              legal_status_source={act.legal_status_source}
              legal_status_reference={act.legal_status_reference}
              legal_status_url={act.legal_status_url}
              legal_status_checked_at={act.legal_status_checked_at}
              text_version_date={act.text_version_date}
              actId={act.id}
              canEdit={canManageCorpus}
              onUpdated={(fields) => setAct((prev) => (prev ? ({ ...prev, ...fields } as LegalAct) : prev))}
              onError={() => setError(t('legalActs.errors.statusUpdateFailed'))}
            />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {[act.number, act.short_title].filter(Boolean).join(' · ')}
          </p>
          {versions.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span className="text-sm text-gray-500">{t('legalActs.versions')}:</span>
              {versions.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setCurrentId(v.id)}
                  className={`text-xs font-medium border rounded-full px-2 py-0.5 ${
                    v.id === currentId
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t('legalActs.languages.' + v.language)}
                </button>
              ))}
            </div>
          )}
          {act.official_url && (
            <a
              href={act.official_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline mt-1"
            >
              <ExternalLink size={14} />
              {t('legalActs.detail.officialLink')}
            </a>
          )}
          <OfficialSourceNotice className="mt-2" sources={[officialSource]} url={act.official_url} />
          {act.jurisdiction === 'INT' && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{t('legalActs.detail.treatyLanguagesNotice')}</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleAttachToggle}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border ${
              isAttached ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Paperclip size={16} />
            {isAttached ? t('common.detach') : t('common.attach')}
          </button>
          {officialSource === 'fedlex' && (
            <button
              type="button"
              onClick={() => downloadLegalActFile(act.id)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Download size={16} />
              {t('legalActs.detail.downloadXml')}
            </button>
          )}
          {canManageCorpus && (
            <button
              type="button"
              onClick={handleDeleteAct}
              disabled={isDeletingAct}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={16} />
              {t('legalActs.detail.deleteAct')}
            </button>
          )}
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div>
        <h3 className="text-lg font-medium mb-3">{t('legalActs.detail.articlesTitle')}</h3>
        <VirtualizedArticleList
          articles={articles}
          renderActions={canManageCorpus ? (article) => (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => openEditArticle(article as LegalArticle)}
                aria-label={t('legalActs.detail.editArticle')}
                title={t('legalActs.detail.editArticle')}
                className="p-1 rounded-full text-gray-500 hover:text-primary-600 hover:bg-primary-50"
              >
                <Pencil size={16} />
              </button>
              <button
                type="button"
                onClick={() => handleDeleteArticle(article as LegalArticle)}
                aria-label={t('legalActs.detail.deleteArticle')}
                title={t('legalActs.detail.deleteArticle')}
                className="p-1 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ) : undefined}
        />
      </div>

      {editingArticle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{t('legalActs.detail.editArticle')}</h3>
              <button type="button" onClick={() => setEditingArticle(null)} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <label className="block text-sm">
                <span className="text-gray-600">{t('legalActs.detail.fieldLabel')}</span>
                <input
                  type="text"
                  value={editForm.label}
                  onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">{t('legalActs.detail.fieldRubric')}</span>
                <input
                  type="text"
                  value={editForm.rubric}
                  onChange={(e) => setEditForm({ ...editForm, rubric: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">{t('legalActs.detail.fieldSection')}</span>
                <input
                  type="text"
                  value={editForm.section_title}
                  onChange={(e) => setEditForm({ ...editForm, section_title: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">{t('legalActs.detail.fieldContent')}</span>
                <textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  rows={8}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={() => setEditingArticle(null)} className="px-3 py-1.5 rounded-md border text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveArticle}
                disabled={isSavingArticle}
                className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
